// Detecção de termos proibidos no frontend.
// Espelha a lógica do backend (DetectForbiddenTermsService): correspondência por
// termo exato, ignorando maiúsculas/minúsculas e acentos, respeitando limites de
// palavra, suportando frases (ex.: "não tem") e reconhecendo o plural das palavras.

const normalize = value =>
  (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Gera as formas plurais (com acento) de uma palavra seguindo regras comuns do
// PT-BR. Inclui a própria palavra (singular). Best-effort para casos irregulares.
const pluralForms = word => {
  const forms = new Set();
  if (!word) return forms;
  forms.add(word);
  const lower = word.toLowerCase();
  const cut = n => word.slice(0, word.length - n);

  if (/ão$/.test(lower)) {
    forms.add(cut(2) + "ões");
    forms.add(cut(2) + "ães");
    forms.add(cut(2) + "ãos");
  } else if (/[aeiouáéíóúâêôãõ]$/.test(lower)) {
    forms.add(word + "s"); // casa->casas, erro->erros
  } else if (/[rz]$/.test(lower)) {
    forms.add(word + "es"); // flor->flores, luz->luzes
  } else if (/m$/.test(lower)) {
    forms.add(cut(1) + "ns"); // homem->homens, bom->bons
  } else if (/il$/.test(lower)) {
    forms.add(cut(2) + "is"); // barril->barris
  } else if (/l$/.test(lower)) {
    forms.add(cut(1) + "is"); // animal->animais, papel->papeis
  } else if (/s$/.test(lower)) {
    forms.add(word + "es"); // mês->meses (invariáveis já cobertas pela base)
  } else {
    forms.add(word + "s");
    forms.add(word + "es");
  }
  return forms;
};

// Formas (com acento) do termo, pluralizando apenas a última palavra (suporta
// frases). Ordenadas da mais longa para a mais curta, para casar o plural antes
// do singular.
const termForms = term => {
  const trimmed = (term || "").trim();
  if (!trimmed) return [];
  const parts = trimmed.split(/\s+/);
  const last = parts.pop();
  const prefix = parts.join(" ");
  const forms = Array.from(pluralForms(last)).map(f =>
    prefix ? `${prefix} ${f}` : f
  );
  return Array.from(new Set(forms)).sort((a, b) => b.length - a.length);
};

/**
 * Retorna os termos proibidos encontrados no texto (singular ou plural).
 * @param {string} text
 * @param {Array<{id:number, term:string, replacement:string|null}>} terms
 * @returns {Array<{id:number, term:string, replacement:string|null}>}
 */
export const detectForbiddenTerms = (text, terms) => {
  if (!text || !Array.isArray(terms) || !terms.length) {
    return [];
  }

  const normalizedText = normalize(text);
  const matches = [];
  const seen = new Set();

  terms.forEach(term => {
    const singularKey = normalize(term.term).trim();
    if (!singularKey || seen.has(singularKey)) {
      return;
    }

    const forms = Array.from(
      new Set(
        termForms(term.term)
          .map(f => normalize(f).trim())
          .filter(Boolean)
      )
    );
    if (!forms.length) {
      return;
    }

    const alt = forms.map(escapeRegex).join("|");
    const pattern = new RegExp(
      `(^|[^\\p{L}\\p{N}])(?:${alt})(?=$|[^\\p{L}\\p{N}])`,
      "u"
    );

    const found = pattern.exec(normalizedText);
    if (found) {
      seen.add(singularKey);
      matches.push({
        id: term.id,
        term: term.term,
        replacement: term.replacement || null,
        // Posição da 1ª ocorrência no texto, para ordenar na sequência de uso.
        index: found.index
      });
    }
  });

  // Ordena pela ordem em que os termos aparecem na mensagem (não alfabética).
  matches.sort((a, b) => a.index - b.index);

  return matches.map(({ index, ...rest }) => rest);
};

// Ajusta a primeira letra de `target` para o mesmo caso (maiúscula/minúscula) da
// primeira letra da ocorrência real `source`. Como não sabemos se a palavra está
// no início da frase ou no meio, a substituição herda o caso do que foi digitado.
const matchFirstLetterCase = (source, target) => {
  if (!source || !target) return target;
  const s = source.charAt(0);
  const isUpper = s !== s.toLowerCase() && s === s.toUpperCase();
  const isLower = s !== s.toUpperCase() && s === s.toLowerCase();
  if (isUpper) {
    return target.charAt(0).toUpperCase() + target.slice(1);
  }
  if (isLower) {
    return target.charAt(0).toLowerCase() + target.slice(1);
  }
  return target;
};

/**
 * Aplica as substituições sugeridas ao texto original, cobrindo ocorrências no
 * singular e no plural. A primeira letra da substituição herda o caso da
 * ocorrência encontrada; se a ocorrência estava no plural, a substituição (quando
 * é uma única palavra) também é pluralizada com "s" (best-effort). Termos sem
 * substituição são mantidos. Acento: best-effort.
 * @param {string} text
 * @param {Array<{term:string, replacement:string|null}>} matches
 * @returns {string}
 */
export const applyReplacements = (text, matches) => {
  if (!text || !Array.isArray(matches) || !matches.length) {
    return text;
  }

  let result = text;
  matches.forEach(({ term, replacement }) => {
    if (!replacement) {
      return;
    }
    const forms = termForms(term); // formas acentuadas (singular + plurais)
    if (!forms.length) {
      return;
    }
    const singularNorm = normalize((term || "").trim());
    const alt = forms.map(escapeRegex).join("|");
    // Limite de palavra unicode ao redor do termo/plural (texto original).
    const pattern = new RegExp(
      `(^|[^\\p{L}\\p{N}])(${alt})(?=$|[^\\p{L}\\p{N}])`,
      "giu"
    );
    result = result.replace(pattern, (_m, prefix, matched) => {
      let repl = replacement;
      // Se a ocorrência estava no plural, pluraliza a substituição de uma palavra.
      const isPlural = normalize(matched).trim() !== singularNorm;
      if (isPlural && !/\s/.test(repl) && !/s$/i.test(repl)) {
        repl = `${repl}s`;
      }
      repl = matchFirstLetterCase(matched, repl);
      return `${prefix}${repl}`;
    });
  });

  return result;
};
